import { inject, Injectable, OnDestroy } from "@angular/core";
import { BehaviorSubject, combineLatest, firstValueFrom, Observable, of, Subject } from "rxjs";
import { PaysafeResult } from "../models/paysafe-result";
import { FiatTransaction } from "@models/transaction";
import { Router } from "@angular/router";
import { ApicallOption, ApicallService } from "@core-services/apicall.service";
import { catchError, filter, map, mapTo, take, tap, switchMap } from "rxjs/operators";
import { PaysafeError } from "../models/paysafe-error";
import { PaysafeFieldError } from "../models/paysafe-field-error";
import { ButtonTextKeys } from "@modules/paysafe/models/button-text.type";
import { ResponseError, ErrorClass } from "bp-common-models-lib";
import { config } from "config/environment";
import { TranslationService } from "@core-services/translation.service";
import {
	PAYSAFE_ERRORS,
	PAYSAFE_PROCESSING_FIRST_STEP_DESCRIPTION_I18N_KEY,
	PAYSAFE_PROCESSING_SECOND_STEP_DESCRIPTION_I18N_KEY,
	PAYSAFE_TIMEOUT_COUNT,
	paysafeInitialisationConfig
} from "@modules/paysafe/models/paysafe-config";
import { CreditCardTransactionStatus } from "@modules/paysafe/models/credit-card-transaction-status";
import { CreditCardDeposit } from "@modules/paysafe/models/credit-card-deposit";
import { PaysafeResponseDetails } from "@modules/paysafe/models/paysafe-response-details";
import { CheckoutDepositWebsocketStateService } from "@modules/checkout/services/checkout-deposit-websocket-state.service";
import { ModalService } from "@core-services/modal.service";
import { CheckoutDepositData } from "@modules/checkout/entities/checkout.deposit-data";
import { NewProfileFacade } from "@modules/main/components/profile/new-profile/state/new-profile.facade";
import { ProfileModel } from "@modules/main/components/profile/new-profile/entities/profile-model";
import { UserService } from "@main-services/user.service";

@Injectable()
export abstract class PaysafeService implements OnDestroy {
	protected pendingProcess: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
	protected showRedirection: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
	public pending$: Observable<boolean> = this.pendingProcess.asObservable();
	public showRedirection$: Observable<boolean> = this.showRedirection.asObservable();
	protected readonly PAYSAFE_COUNTER: number = PAYSAFE_TIMEOUT_COUNT;
	protected token: string = null;
	protected script: HTMLScriptElement = null;
	protected paysafeInitialization = null;
	protected paysafeInstance = null;
	protected processEnded: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
	protected processIdle: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(true);
	public disableButton: Observable<boolean> = combineLatest([
		this.processIdle,
		this.processEnded.pipe(map((finish: boolean) => !finish))
	]).pipe(map((values: [boolean, boolean]) => !values.includes(false)));
	protected expired: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
	public expired$: Observable<boolean> = this.expired.asObservable();
	protected buttonText: BehaviorSubject<ButtonTextKeys> = new BehaviorSubject<ButtonTextKeys>("Continue");
	protected ccSave: boolean = false;
	protected transaction: FiatTransaction = null;
	protected transactionId: string;
	protected paysafeAccountId: number = null;
	protected fiatSymbol: string = null;
	protected walletId: string = null;
	protected totalAmount: number = null;
	protected _errors$: Subject<string[]> = new Subject<string[]>();
	public errors$: Observable<string[]> = this._errors$.asObservable();
	private pendingScreenTextKey$: BehaviorSubject<string> = new BehaviorSubject<string>(
		PAYSAFE_PROCESSING_FIRST_STEP_DESCRIPTION_I18N_KEY
	);
	public pendingScreenDescription$: Observable<string> = this.pendingScreenTextKey$.asObservable();
	public checkoutDepositWebsocketStateService = inject(CheckoutDepositWebsocketStateService);

	protected constructor(
		public router: Router,
		public apiCallService: ApicallService,
		public translationService: TranslationService,
		public modalService: ModalService,
		public profileFacade: NewProfileFacade,
		public userService: UserService
	) {}

	public get buttonText$(): Observable<ButtonTextKeys> {
		return this.buttonText.asObservable();
	}

	public setToken(token: string): void {
		this.token = token;
	}

	public setTransactionId(transactionId: string): void {
		this.transactionId = transactionId;
	}

	public ngOnDestroy(): void {
		if (this.script) {
			document.getElementsByTagName("head")[0].removeChild(this.script);
		}
		window["paysafe"] = undefined;
	}
	// TODO: eslint fix
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	public importScript(): Promise<any> {
		// TODO: eslint fix
		// eslint-disable-next-line @typescript-eslint/no-explicit-any
		return new Promise<any>((resolve, reject) => {
			this.script = document.createElement("script");
			this.script.src = config.customProviders["paysafe"].scriptUrl;
			this.script.type = "text/javascript";
			this.script.async = false;
			this.script.defer = true;
			this.script.onerror = ($event: Event | string) => {
				console.error("[PAYSAFE] ", $event);
				reject($event);
			};

			this.script.onload = ($event: Event) => {
				this.paysafeInitialization = window["paysafe"];
				this.initializeFields3DS(resolve, reject);
			};
			document.getElementsByTagName("head")[0].appendChild(this.script);
		});
	}

	public runTokenization = (
		cardHolderNameInput: string,
		instance,
		isCheckout: boolean,
		checkoutDepositData: CheckoutDepositData
	): void => {
		this.resetErrors();
		this.processIdle.next(false);
		if (!isCheckout) {
			this.displayProcessingScreen();
		}
		if (this.userService.user.isLoggedIn) {
			this.profileFacade
				.getProfile$()
				.pipe(
					take(1),
					map((profile: ProfileModel) => {
						const payload = {
							threeDS: {
								amount: this.totalAmount * 100,
								currency: this.fiatSymbol,
								accountId: this.paysafeAccountId,
								useThreeDSecureVersion2: true,
								authenticationPurpose: "PAYMENT_TRANSACTION",
								profile: {
									email: profile.email,
									phone: profile.mobile
								}
							},
							vault: {
								holderName: cardHolderNameInput
							}
						};
						return payload;
					}),
					switchMap((payload) =>
						this.getCreatedTransaction().pipe(
							take(1),
							map((transaction: CreditCardDeposit<PaysafeResponseDetails>) => ({
								payload,
								transaction
							})),
							tap(({ payload, transaction }) => {
								const submitFunction = isCheckout
									? this.handleCheckoutSubmit(payload.vault.holderName, checkoutDepositData)
									: this.handleSubmit(payload.vault.holderName);

								instance.tokenize(payload, submitFunction);
							})
						)
					)
				)
				.subscribe();
		} else {
			const payload = {
				threeDS: {
					amount: this.totalAmount * 100,
					currency: this.fiatSymbol,
					accountId: this.paysafeAccountId,
					useThreeDSecureVersion2: true,
					authenticationPurpose: "PAYMENT_TRANSACTION"
				},
				vault: {
					holderName: cardHolderNameInput
				}
			};
			this.getCreatedTransaction().subscribe((transaction: CreditCardDeposit<PaysafeResponseDetails>) => {
				const submitFunction = isCheckout
					? this.handleCheckoutSubmit(payload.vault.holderName, checkoutDepositData)
					: this.handleSubmit(payload.vault.holderName);
				instance.tokenize(payload, submitFunction);
			});
		}
	};

	public async userIsFromUK(): Promise<boolean | null> {
		if (!this.userService.user.isLoggedIn) {
			return false;
		}

		try {
			const profile = await firstValueFrom(this.profileFacade.getProfile$());
			return profile.iso === "GB";
		} catch (e) {
			console.error("Error fetching user profile:", e);
			return false;
		}
	}

	private showCheckoutProcessingScreen(checkoutDepositData: CheckoutDepositData): void {
		this.modalService.popAll();
		this.checkoutDepositWebsocketStateService.showProcessingScreen();
		this.checkoutDepositWebsocketStateService.initSocketService(checkoutDepositData);
	}

	private handleCheckoutSubmit = (
		cardHolderName: string,
		checkoutDepositData: CheckoutDepositData
	): ((instance: never, error: Error, result: PaysafeResult) => Promise<void>) =>
		this.handleCheckoutResponse(cardHolderName, checkoutDepositData);

	private handleCheckoutResponse =
		(cardHolderName: string, checkoutDepositData: CheckoutDepositData) =>
		async (instance, error: PaysafeError, result: PaysafeResult): Promise<void> => {
			this.showCheckoutProcessingScreen(checkoutDepositData);
			await this.processResponseFromCheckout(cardHolderName, result, checkoutDepositData);
		};

	public handleSubmit = (
		cardHolderName: string
		// TODO: eslint fix
	): ((instance: never, error: Error, result: PaysafeResult) => Promise<void>) => this.handleResponse(cardHolderName);

	public handleResponse =
		(cardHolderName: string) =>
		async (instance, error: PaysafeError, result: PaysafeResult): Promise<void> => {
			this.processIdle.next(true);
			if (error) {
				return this.handleResponseError(error);
			}
			this.displayPendingScreen();
			this.processEnded.next(true);
			this.buttonText.next("Continue");
			this.processResponse(cardHolderName, result);
		};

	private async processResponseFromCheckout(
		cardHolderName: string,
		result: PaysafeResult,
		checkoutDepositData: CheckoutDepositData
	): Promise<void> {
		const status: boolean | ResponseError[] = await this.sendConfirmation(cardHolderName, result);
		if (status instanceof Array) {
			const respError = status[0];
			if (respError.code === "fwt.paysafe.credit_category.disabled") {
				this.checkoutDepositWebsocketStateService.clearTimeout();
				this.checkoutDepositWebsocketStateService.unsubscribeFromSockets();
				this.checkoutDepositWebsocketStateService.showVisaCreditCardNotAccepted(checkoutDepositData);
			}
			return;
		}
	}

	public resetErrors(): void {
		this._errors$.next(null);
	}

	public changeSave(value: boolean): void {
		this.ccSave = value;
	}

	public initDepositData(): Observable<CreditCardDeposit<PaysafeResponseDetails>> {
		return this.getCreatedTransaction().pipe(
			take(1),
			tap((response: CreditCardDeposit<PaysafeResponseDetails>) => {
				this.paysafeAccountId = +response.options.accountId;
				this.fiatSymbol = response.fiat_symbol;
				this.totalAmount = +response.total_amount;
				this.walletId = response.fiat_wallet_id;
			})
		);
	}

	public setTransactionDetails(transaction: FiatTransaction): void {
		this.transaction = transaction;
		this.transactionId = transaction.getId();
	}

	public getCreatedTransaction(): Observable<CreditCardDeposit<PaysafeResponseDetails>> {
		return this.getDeposit().pipe(
			catchError((error) => of({ status: "canceled" })),
			tap((response: CreditCardDeposit<PaysafeResponseDetails>) => {
				if (response.status === "canceled") {
					this.expired.next(true);
					this.pendingProcess.next(false);
				}
			}),
			filter((response: CreditCardDeposit<PaysafeResponseDetails>) => response.status !== "canceled")
		);
	}

	public updateDepositData(data: {
		ccToken: string;
		ccHolderName: string;
		ccSave: boolean;
	}): Promise<boolean | ResponseError[]> {
		const url: string = `credit-card-deposit/${this.transactionId}/${this.token}`;

		return this.apiCallService
			.post(url, new ApicallOption(null, data))
			.pipe(
				mapTo(true),
				catchError((err: ErrorClass) => of(err.responseErrors))
			)
			.toPromise();
	}

	protected displayPendingScreen(): void {
		this.pendingScreenTextKey$.next(PAYSAFE_PROCESSING_SECOND_STEP_DESCRIPTION_I18N_KEY);
	}

	protected abstract processResponse(cardHolderName: string, result: PaysafeResult): void;

	protected canShowEndScreen({ status, approved }: CreditCardTransactionStatus): boolean {
		if (status === "got_payment" && !approved) {
			return true;
		}
		return ["finished", "canceled", "potential_fraud"].includes(status);
	}

	protected getPendingTransactionStatus(): Promise<CreditCardTransactionStatus> {
		return this.getDeposit()
			.pipe(
				map((response: CreditCardDeposit<PaysafeResponseDetails>) => ({
					status: response.status,
					approved: response.approved
				}))
			)
			.toPromise();
	}

	protected sendConfirmation(cardHolderName: string, result: PaysafeResult): Promise<boolean | ResponseError[]> {
		return this.updateDepositData({
			ccToken: result.token,
			ccHolderName: cardHolderName,
			ccSave: this.ccSave
		});
	}

	// TODO: eslint fix
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	protected abstract displayProcessingScreen(): any;

	protected abstract returnToFormScreen(): void;

	protected handleResponseError(error: PaysafeError): void {
		console.error(error);
		this.buttonText.next("try_again");
		this.returnToFormScreen();
		switch (error.code as PAYSAFE_ERRORS) {
			case PAYSAFE_ERRORS.INVALID_FIELDS:
				this.handleFieldErrors(error);
				break;
			case PAYSAFE_ERRORS.CONNECTION_TO_SERVER:
			case PAYSAFE_ERRORS.COMMUNICATION_WITH_SERVER:
				this.handleConnectionError();
				break;
			case PAYSAFE_ERRORS.AUTH_NOT_ACCEPTED:
			case PAYSAFE_ERRORS.USER_ABORTED_AUTH:
			case PAYSAFE_ERRORS.ENROLLMENT_NOT_ACCEPTED:
				this.handleAuthenticationError(error);
				break;
			default:
				this.handleUnexpectedError(error);
				break;
		}
	}

	protected allowSuccess({ status, approved }: CreditCardTransactionStatus): boolean {
		return status === "finished" || (status === "got_payment" && !approved) || status === "potential_fraud";
	}

	protected resetServiceState(): void {
		this.paysafeInitialization = null;
		this.paysafeInstance = null;
		this.processEnded.next(false);
		this.processIdle.next(true);
		this.pendingProcess.next(false);
		this.buttonText.next("Continue");
		this.pendingScreenTextKey$.next(PAYSAFE_PROCESSING_FIRST_STEP_DESCRIPTION_I18N_KEY);
		this.ccSave = false;
	}

	private getDeposit(): Observable<CreditCardDeposit<PaysafeResponseDetails>> {
		const url: string = `credit-card-deposit/${this.transactionId}/${this.token}`;

		return (
			this.apiCallService
				.get(url)
				// TODO: eslint fix
				// eslint-disable-next-line @typescript-eslint/no-explicit-any
				.pipe(map((response: any) => response.attributes as CreditCardDeposit<PaysafeResponseDetails>))
		);
	}

	private initializeFields3DS(resolve, reject): void {
		const options = paysafeInitialisationConfig(config, this.translationService);
		this.initDepositData().subscribe((response: CreditCardDeposit<PaysafeResponseDetails>) => {
			this.paysafeInitialization.fields.setup(
				response.options.sut,
				options,
				this.handleInit(resolve, reject, response)
			);
		});
	}

	private handleInit =
		(resolve, reject, response): (({ instance, transactionResponse }, error: Error) => void) =>
		(instance, error) => {
			if (error) {
				console.error(error);
				reject(error);
			}
			this.paysafeInstance = instance;
			resolve({ instance, response });
		};

	private handleAuthenticationError(error: PaysafeError): void {
		this.setFormError(this.translationService.translate("authentication_error", [error.code]));
	}

	private handleConnectionError(): void {
		this.setFormError(this.translationService.translate("connection_error"));
	}

	private setFormError(error: string): void {
		this._errors$.next([error]);
	}

	private handleUnexpectedError(error: PaysafeError): void {
		this.setFormError(this.translationService.translate("unexpected_error", [error.code]));
	}

	private handleFieldErrors(error: PaysafeError): void {
		let errors = error.fieldErrors.map((field: PaysafeFieldError) => this.parseField(field));
		this._errors$.next(errors);
	}

	private parseField({ field, message }: PaysafeFieldError): string {
		const fieldStrI18n = this.translationService.translate("field");
		const fieldMap = {
			cardExpiry: this.translationService.translate("expiration_date"),
			cardNum: this.translationService.translate("card_number"),
			cvv: this.translationService.translate("cvv"),
			holderName: this.translationService.translate("name_on_card")
		};

		return `${fieldStrI18n} ${fieldMap[field]}: ${message}`;
	}
}
